import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { me, api } from '@/api';
import { Search, CreditCard, Settings, RefreshCw, MapPin, Users, Eye, Phone, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/auth';
import LanguageSelector from '@/components/LanguageSelector';

const LOGO_URL = '/logo.png';

function StatCard({ icon: Icon, label, value, color, bgColor, onClick }) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper 
      onClick={onClick}
      className={`bg-white rounded-2xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-all w-full text-left ${onClick ? 'cursor-pointer hover:scale-[1.02]' : ''}`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${bgColor}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
        <div>
          <div className="text-3xl font-bold text-slate-900">{value}</div>
          <div className="text-sm text-slate-500">{label}</div>
        </div>
      </div>
    </Wrapper>
  );
}

function ActionCard({ icon: Icon, title, description, onClick, gradient }) {
  return (
    <button 
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl p-6 border border-slate-200 shadow-sm hover:shadow-lg transition-all hover:scale-[1.02] group"
    >
      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 ${gradient}`}>
        <Icon className="w-7 h-7 text-white" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 group-hover:text-[#5848FF] transition-colors">
        {title}
      </h3>
      <p className="text-sm text-slate-500 mt-1">{description}</p>
    </button>
  );
}

function RequestPreview({ request, onContact }) {
  const pref = request.contactPreference || 'whatsapp';
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="font-semibold text-slate-900 truncate flex-1">
          {request.title || 'Richiesta'}
        </h3>
        {request.urgent && (
          <span className="px-2 py-1 rounded-full text-[10px] font-semibold bg-red-100 text-red-700">
            Urgente
          </span>
        )}
      </div>
      <p className="text-sm text-slate-600 line-clamp-2 mb-3">{request.description}</p>
      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-4">
        <MapPin className="w-4 h-4" />
        <span>{request.address?.city || '—'}</span>
      </div>
      <div className="flex gap-2">
        {pref === 'whatsapp' ? (
          <Button size="sm" className="flex-1 rounded-lg bg-green-500 hover:bg-green-600 text-white text-[13px]" onClick={() => onContact(request, 'whatsapp')}>
            <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
          </Button>
        ) : pref === 'telefono' ? (
          <Button size="sm" className="flex-1 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-[13px]" onClick={() => onContact(request, 'call')}>
            <Phone className="w-4 h-4 mr-1" /> Chiama
          </Button>
        ) : (
          <Button size="sm" className="flex-1 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-[13px]" onClick={() => onContact(request, 'email')}>
            <MessageCircle className="w-4 h-4 mr-1" /> Email
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CompanyDashboard(){
  const { t } = useLanguage();
  const { logout } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({ openRequests: 0, contactsMade: 0, viewedRequests: 0 });
  const [recentRequests, setRecentRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = async () => {
    try {
      const profileRes = await me();
      const profileData = profileRes.data;
      
      if (profileData?.plan?.status === 'pending_payment') {
        toast.error('Completa il pagamento per attivare l\'abbonamento');
        nav('/ditta/payment-callback?canceled=true');
        return;
      }
      
      setProfile(profileData);
      
      // Load open requests
      const reqRes = await api.get('/company/requests/open');
      const requests = reqRes.data || [];
      
      setStats({
        openRequests: requests.length,
        contactsMade: 0, // TODO: track contacts made by this company
        viewedRequests: 0
      });
      
      setRecentRequests(requests.slice(0, 4));
    } catch (err) {
      console.error('Error loading dashboard:', err);
    }
  };

  const refresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  useEffect(() => {
    setLoading(true);
    loadData().finally(() => setLoading(false));
  }, []);

  const handleContact = async (request, type) => {
    try {
      const res = await api.post(`/requests/${request.id}/contact`, {
        requestId: request.id,
        contactType: type
      });
      
      const { contactPreference, contactValue } = res.data;
      
      if (!contactValue) {
        toast.error('Recapito non disponibile');
        return;
      }
      
      if (type === 'whatsapp') {
        const phone = contactValue.replace(/\s/g, '').replace(/^\+/, '');
        const message = encodeURIComponent(`Ciao, ti contatto da ServiceGo per la tua richiesta: ${request.title || 'Richiesta'}`);
        window.open(`https://wa.me/${phone}?text=${message}`, '_blank');
      } else {
        window.open(`tel:${contactValue}`, '_blank');
      }
      
      toast.success('Contatto registrato!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster />
      
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="ServiceGo" className="h-10 w-auto" />
            </Link>
            
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => nav('/ditta/impostazioni')}
                className="text-slate-600"
              >
                <Settings className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={logout}
                className="text-slate-600"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
              Ciao, {profile?.companyName || 'Ditta'}! 👋
            </h1>
            <p className="text-slate-500 mt-1">Trova nuovi clienti nella tua zona</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={refresh}
              disabled={refreshing}
              className="rounded-xl"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </Button>
            <Button 
              onClick={() => nav('/ditta/richieste')}
              className="rounded-xl bg-[#5848FF] hover:bg-[#4638EF] text-white shadow-lg shadow-indigo-200"
            >
              <Search className="w-4 h-4 mr-2" />
              Cerca Richieste
            </Button>
          </div>
        </div>

        {/* Plan Status */}
        {profile?.plan && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-6 text-white mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <p className="text-emerald-100 text-sm">Il tuo piano</p>
                <h2 className="text-2xl font-bold">
                  {profile.plan.status === 'trialing' ? 'Prova Gratuita' : 'Piano Attivo'}
                </h2>
                {profile.plan.status === 'trialing' && profile.plan.trial_ends_at && (
                  <p className="text-emerald-100 mt-1">
                    Scade il {new Date(profile.plan.trial_ends_at).toLocaleDateString('it-CH')}
                  </p>
                )}
              </div>
              <Button 
                onClick={() => nav('/ditta/abbonamento')}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Gestisci
              </Button>
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <StatCard 
            icon={Search} 
            label="Richieste Disponibili" 
            value={stats.openRequests}
            color="text-indigo-600"
            bgColor="bg-indigo-100"
            onClick={() => nav('/ditta/richieste')}
          />
          <StatCard 
            icon={Phone} 
            label="Contatti Effettuati" 
            value={stats.contactsMade}
            color="text-green-600"
            bgColor="bg-green-100"
          />
          <StatCard 
            icon={Eye} 
            label="Richieste Visualizzate" 
            value={stats.viewedRequests}
            color="text-blue-600"
            bgColor="bg-blue-100"
          />
        </div>

        {/* Actions */}
        <h2 className="text-xl font-semibold text-slate-900 mb-4">Azioni Rapide</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <ActionCard 
            icon={Search}
            title="Cerca Richieste"
            description="Trova clienti nella tua zona e categoria"
            onClick={() => nav('/ditta/richieste')}
            gradient="bg-gradient-to-br from-indigo-500 to-purple-600"
          />
          <ActionCard 
            icon={CreditCard}
            title="Abbonamento"
            description="Gestisci il tuo piano e fatture"
            onClick={() => nav('/ditta/abbonamento')}
            gradient="bg-gradient-to-br from-emerald-500 to-teal-600"
          />
          <ActionCard 
            icon={Settings}
            title="Impostazioni"
            description="Modifica il profilo della tua ditta"
            onClick={() => nav('/ditta/impostazioni')}
            gradient="bg-gradient-to-br from-slate-500 to-slate-700"
          />
        </div>

        {/* Recent Requests */}
        {recentRequests.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-slate-900">Ultime Richieste</h2>
              <Button 
                variant="link" 
                onClick={() => nav('/ditta/richieste')}
                className="text-[#5848FF]"
              >
                Vedi tutte →
              </Button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentRequests.map(req => (
                <RequestPreview 
                  key={req.id} 
                  request={req} 
                  onContact={handleContact}
                />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
